import React, { FC, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'reactstrap';

import { downloadLink } from 'utils/util/util';

interface Document {
  id: number;
  deleted_at: string | null;
  editable: boolean;
  media: {
    file: string;
  };
  media_history?: Array<{
    id: number;
    file: string;
    created_at: string;
  }> | null;
}

const MoreActionsButton: FC<{
  document: Document;
  onEditClick: (documentId: number) => void;
  onDeleteClick: (documentId: number) => void;
  onShowHistoryClick: (documentId: number) => void;
}> = ({ document, onEditClick, onDeleteClick, onShowHistoryClick }) => {
  const { formatMessage } = useIntl();

  const handleEditClick = useCallback(() => {
    onEditClick(document.id);
  }, [document.id, onEditClick]);

  const handleDeleteClick = useCallback(() => {
    onDeleteClick(document.id);
  }, [document.id, onDeleteClick]);

  const handleDownloadClick = useCallback(() => {
    downloadLink({
      filename: document.media.file.split('/').pop() || 'document',
      url: document.media.file,
    });
  }, [document.media.file]);

  const handleShowHistoryClick = useCallback(() => {
    onShowHistoryClick(document.id);
  }, [document.id, onShowHistoryClick]);

  const isDeleted = !!document.deleted_at;
  const isEditable = document.editable;
  const hasHistory =
    document.media_history && document.media_history.length > 0;

  return (
    <div className="d-flex gap-1">
      {isEditable && (
        <Button
          size="sm"
          color="white"
          onClick={handleEditClick}
          title={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.more_actions.edit',
            defaultMessage: 'Edit this document',
          })}
        >
          <i className="fe fe-edit" />
        </Button>
      )}
      {isEditable && !isDeleted && (
        <Button
          size="sm"
          color="white"
          onClick={handleDeleteClick}
          title={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.more_actions.delete',
            defaultMessage: 'Delete this document',
          })}
        >
          <i className="fe fe-trash" />
        </Button>
      )}
      <Button
        size="sm"
        color="white"
        onClick={handleDownloadClick}
        title={formatMessage({
          id: 'app.views.administration.content_administration.documents_admin.more_actions.download',
          defaultMessage: 'Download this document',
        })}
      >
        <i className="fe fe-download" />
      </Button>
      {isEditable && hasHistory && (
        <Button
          size="sm"
          color="white"
          onClick={handleShowHistoryClick}
          title={formatMessage({
            id: 'app.views.administration.content_administration.documents_admin.more_actions.show_history',
            defaultMessage: 'Show version history',
          })}
        >
          <i className="fe fe-clock" />
        </Button>
      )}
    </div>
  );
};

export default React.memo(MoreActionsButton);
